import React, { useContext, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/standard-button';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { ClientNetworkStatus } from '@confluence/legacy-schema-bridge/entry-points/useNetworkStatusListenerCache.ts';

function NetworkStatusFlag({ flags, status }: NetworkStatusFlagProps) {
	const { match: route } = useContext(RoutesContext);
	let messages: typeof ipBlockedMessages | typeof rateLimitedMessages | undefined;

	switch (status) {
		case ClientNetworkStatus.IP_BLOCKED:
			// Derisk the flag by not displaying it on full-page editors because they
			// (may) have an EditorConnectionBanner:
			if (!route?.name?.startsWith('EDIT_')) {
				messages = ipBlockedMessages;
			}
			break;
		case ClientNetworkStatus.RATE_LIMITED:
			messages = rateLimitedMessages;
			break;
	}

	useEffect(() => {
		if (messages) {
			void flags.showErrorFlag({
				id: `NetworkStatusFlag-${status}`,
				title: <FormattedMessage {...messages.title} />,
				description: (
					<FormattedMessage
						{...messages.description}
						values={{
							refreshLink: (
								<Button appearance="link" onClick={reloadPage} spacing="none">
									<FormattedMessage {...messages.refreshLinkText} />
								</Button>
							),
						}}
					/>
				),
			});
		}
	}, [flags, messages, status]);

	return null;
}

type NetworkStatusFlagProps = WithFlagsProps & {
	status?: ClientNetworkStatus | null;
};

// Reduce critical path bundle/file size:
// eslint-disable-next-line import/no-default-export
export default withFlags(NetworkStatusFlag);

const ipBlockedMessages = defineMessages({
	description: {
		id: 'network-status-listener.flag.description.ip-blocked',
		defaultMessage:
			'Check your network connection, {refreshLink}, and try again. If the problem persists, contact your administrator for help.',
		description: 'Contents of flag notifying that rate limiting has happened',
	},
	refreshLinkText: {
		id: 'network-status-listener.flag.refresh-link-text.ip-blocked',
		defaultMessage: 'refresh',
		description:
			'Text of button/link the user can click to refresh the current page, part of network-status-listener.flag.description.ip-blocked',
	},
	title: {
		id: 'network-status-listener.flag.title.ip-blocked',
		defaultMessage: 'There was a problem accessing this content',
		description: 'Title of flag notifying about network status like offline or rate limiting',
	},
});

const rateLimitedMessages = defineMessages({
	description: {
		id: 'network-status-listener.flag.description.rate-limited',
		defaultMessage:
			'Rate limit reached. Wait a minute or so while the system resets, {refreshLink}, and try again.',
		description: 'Contents of flag notifying that rate limiting has happened',
	},
	refreshLinkText: {
		id: 'network-status-listener.flag.refresh-link-text.rate-limited',
		defaultMessage: 'refresh',
		description:
			'Text of button/link the user can click to refresh the current page, part of network-status-listener.flag.description.rate-limited',
	},
	title: {
		id: 'network-status-listener.flag.title.rate-limited',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: 'We couldn’t load this content',
		description: 'Title of flag notifying about network status like offline or rate limiting',
	},
});

function reloadPage() {
	window.location.reload();
}
